import type { ReactNode } from 'react'
import {
  File,
  FileCode2,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
  XIcon,
} from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { getChatAttachmentPresentation } from '../lib/attachment-presentation'

/**
 * Visual pill used in the chat composer to render an uploaded or in-flight
 * attachment. Shared shape so the upload-progress state visually flows into
 * the attached state without a layout shift — same height, same rounded
 * background, same name truncation. The icon slot varies by state:
 *
 *   - {@link AttachmentPill}: caller supplies any icon node
 *   - {@link UploadingAttachmentPill}: spinner + filename, no remove button
 *   - {@link UploadedAttachmentPill}: file/image icon + filename + remove X
 *
 * Keep the className list in sync across both wrapper helpers — they share
 * the underlying container styles intentionally.
 */
const PILL_CONTAINER_CLASSES =
  'bg-muted hover:bg-muted/80 flex h-7 shrink-0 items-center gap-[5px] rounded-full px-2 py-1.5 transition-colors'

const PILL_TEXT_CLASSES =
  'text-muted-foreground max-w-[120px] truncate text-xs'

const PILL_ICON_CLASSES = 'text-muted-foreground h-3.5 w-3.5'

export type AttachmentPillProps = {
  /** Icon node rendered at the start of the pill. */
  icon: ReactNode
  /** File name; truncated visually beyond ~120px. */
  name: string
  /** Optional short type label, e.g. PDF or DOCX. */
  label?: string
  /** Use subdued styling for labels that are warnings or unsupported states. */
  mutedLabel?: boolean
  /**
   * When provided, renders a trailing X button that calls this on click.
   * Omit for in-flight uploads or any read-only pill.
   */
  onRemove?: () => void
}

export function AttachmentPill({
  icon,
  name,
  label,
  mutedLabel,
  onRemove,
}: AttachmentPillProps) {
  return (
    <div className={PILL_CONTAINER_CLASSES}>
      {icon}
      <span className={PILL_TEXT_CLASSES}>{name}</span>
      {label && (
        <span
          className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase leading-none ${
            mutedLabel
              ? 'bg-muted text-text-subtle'
              : 'bg-card text-text-body'
          }`}
        >
          {label}
        </span>
      )}
      {onRemove && (
        <Button
          aria-label='Remove attachment'
          onClick={onRemove}
          className='size-3.5 !p-0 hover:bg-transparent'
          size='xxsIcon'
          variant='ghost'
        >
          <XIcon className='text-ring h-3.5 w-3.5' />
        </Button>
      )}
    </div>
  )
}

export type UploadedAttachmentPillProps = {
  name: string
  /** MIME type — image/* picks the image icon, anything else picks file. */
  contentType?: string
  onRemove: () => void
}

export function UploadedAttachmentPill({
  name,
  contentType,
  onRemove,
}: UploadedAttachmentPillProps) {
  const presentation = getChatAttachmentPresentation(contentType, name)
  return (
    <AttachmentPill
      icon={<AttachmentIcon kind={presentation.kind} />}
      name={name}
      label={presentation.kind === 'image' ? undefined : presentation.label}
      mutedLabel={!presentation.readableByAi}
      onRemove={onRemove}
    />
  )
}

export type UploadingAttachmentPillProps = {
  name: string
}

export function UploadingAttachmentPill({ name }: UploadingAttachmentPillProps) {
  return (
    <AttachmentPill
      icon={<Loader2 className={`${PILL_ICON_CLASSES} animate-spin`} />}
      name={name}
    />
  )
}

function AttachmentIcon({
  kind,
}: {
  kind: ReturnType<typeof getChatAttachmentPresentation>['kind']
}) {
  if (kind === 'image') return <ImageIcon className={PILL_ICON_CLASSES} />
  if (kind === 'xlsx' || kind === 'csv') {
    return <FileSpreadsheet className={PILL_ICON_CLASSES} />
  }
  if (kind === 'json' || kind === 'md' || kind === 'txt') {
    return <FileCode2 className={PILL_ICON_CLASSES} />
  }
  if (kind === 'pdf' || kind === 'docx') {
    return <FileText className={PILL_ICON_CLASSES} />
  }
  return <File className={PILL_ICON_CLASSES} />
}
